/**
 * /pages — manage public tip / donate / fund pages.
 *
 * List view shows each page's kind, slug, public URL (copyable), enabled
 * toggle, and a "manage" link to edit/disable. "New page" opens a sheet
 * that asks for kind + slug + minimal config, then publishes.
 *
 * Kind shapes (validated server-side, mirrored here for UX):
 *   tip    → suggested amounts (USD), optional title/description
 *   donate → title (required), description, suggested amounts, allow_messages
 *   fund   → title, goal_usd_cents, deadline_ms, description, allow_messages,
 *            leaderboard
 */

import { useEffect, useState } from "react";
import {
  ApiError,
  createPage,
  deletePage,
  deletePageImage,
  listPages,
  updatePage,
  uploadPageImage,
  PAGE_SOCIAL_FIELDS,
  type MerchantPage,
  type PageKind,
  type PageSocial,
} from "../lib/api";
import { Banner, Btn } from "../components/ui";
import { Ic } from "../components/Icons";

const API_BASE: string =
  (import.meta.env.VITE_SHIELDZ_API_URL as string | undefined) ?? "https://shieldz.cash";

/** Resolve a page image_url (a /u/… path) to an absolute, loadable URL. */
const resolveImageUrl = (u: string): string =>
  u.startsWith("/") ? `${API_BASE.replace(/\/+$/, "")}${u}` : u;

const KIND_LABEL: Record<PageKind, string> = {
  tip: "Tip page",
  donate: "Donation page",
  fund: "Fundraiser",
};

const KIND_DESCRIPTION: Record<PageKind, string> = {
  tip: "A simple tip jar. Quick-amount buttons + custom. No buyer info collected.",
  donate: "A public donation page with description and optional message field.",
  fund: "A fundraiser with a goal, deadline, and live progress widget.",
};

const KIND_PATH: Record<PageKind, string> = {
  tip: "tip",
  donate: "donate",
  fund: "fund",
};

export function Pages() {
  const [pages, setPages] = useState<MerchantPage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MerchantPage | null>(null);

  const refetch = async () => {
    try {
      const r = await listPages();
      setPages(r.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refetch();
  }, []);

  if (error) {
    return (
      <div className="scroll">
        <div className="pad">
          <Banner tone="fail">{error}</Banner>
        </div>
      </div>
    );
  }

  if (pages == null) {
    return (
      <div className="scroll">
        <div className="pad" style={{ color: "var(--ink-3)" }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="scroll">
      <div className="pad fade-in" style={{ maxWidth: 720 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
              Public pages
            </h2>
            <p style={{ fontSize: 13.5, color: "var(--ink-3)", margin: "4px 0 0" }}>
              Tip jars, donation pages, and fundraisers. Same direct-to-wallet
              flow as your invoices.
            </p>
          </div>
          <Btn onClick={() => setCreating(true)}>
            <Ic name="plus" size={14} /> New page
          </Btn>
        </div>

        {pages.length === 0 ? (
          <div
            className="card"
            style={{
              padding: 40,
              textAlign: "center",
              color: "var(--ink-3)",
            }}
          >
            <div style={{ fontSize: 14, marginBottom: 4 }}>No pages yet.</div>
            <div style={{ fontSize: 12.5 }}>
              Create a tip jar to put in your Twitter bio, or run a fundraiser
              for a project.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pages.map((p) => (
              <PageRow key={p.id} page={p} onChange={refetch} onEdit={() => setEditing(p)} />
            ))}
          </div>
        )}

        {creating && (
          <NewPageSheet
            onClose={() => setCreating(false)}
            onCreated={() => {
              setCreating(false);
              void refetch();
            }}
          />
        )}

        {editing && (
          <EditPageSheet
            page={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void refetch();
            }}
          />
        )}
      </div>
    </div>
  );
}

function PageRow({
  page,
  onChange,
  onEdit,
}: {
  page: MerchantPage;
  onChange: () => Promise<void> | void;
  onEdit: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(page.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleToggle = async () => {
    setBusy(true);
    try {
      await updatePage(page.id, {
        config: page.config,
        enabled: !page.enabled,
      });
      await onChange();
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Disable ${page.kind}/${page.slug}?`)) return;
    setBusy(true);
    try {
      await deletePage(page.id);
      await onChange();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        padding: 18,
        opacity: page.enabled ? 1 : 0.55,
        display: "flex",
        gap: 14,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: "var(--brand-50)",
          color: "var(--brand-700)",
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          flex: "none",
        }}
      >
        {KIND_PATH[page.kind]}
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {page.config.title ?? page.slug}
        </div>
        <div
          className="mono"
          style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}
        >
          {page.url}
        </div>
      </div>
      <Btn variant="ghost" onClick={() => void handleCopy()}>
        {copied ? "Copied ✓" : "Copy URL"}
      </Btn>
      <Btn variant="ghost" onClick={onEdit}>
        Edit
      </Btn>
      <Btn variant="ghost" onClick={() => void handleToggle()} loading={busy}>
        {page.enabled ? "Disable" : "Enable"}
      </Btn>
      <Btn variant="ghost" onClick={() => void handleDelete()} loading={busy}>
        Delete
      </Btn>
    </div>
  );
}

function NewPageSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [kind, setKind] = useState<PageKind>("tip");
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [goalUsd, setGoalUsd] = useState("");
  const [deadlineDays, setDeadlineDays] = useState("30");
  const [social, setSocial] = useState<PageSocial>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setError(null);

    const cleanSlug = slug.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9_-]{1,62}$/.test(cleanSlug)) {
      setError(
        "Slug must be 2–63 chars, lowercase alphanumeric, hyphens or underscores. Must start with a letter or digit.",
      );
      return;
    }

    // Validate per-kind config minimums
    if (kind !== "tip" && title.trim().length === 0) {
      setError("Title is required for donation and fundraiser pages.");
      return;
    }
    let goalCents: number | undefined;
    let deadlineMs: number | undefined;
    if (kind === "fund") {
      const goal = Number.parseFloat(goalUsd);
      if (!Number.isFinite(goal) || goal < 1 || goal > 1_000_000) {
        setError("Goal must be a number between $1 and $1,000,000.");
        return;
      }
      goalCents = Math.round(goal * 100);
      const days = Number.parseInt(deadlineDays, 10);
      if (!Number.isFinite(days) || days < 1 || days > 365) {
        setError("Deadline must be between 1 and 365 days from now.");
        return;
      }
      deadlineMs = Date.now() + days * 24 * 60 * 60 * 1000;
    }

    setBusy(true);
    try {
      const cleanSocial = cleanSocialMap(social);
      await createPage({
        kind,
        slug: cleanSlug,
        config: {
          ...(title.trim().length > 0 ? { title: title.trim() } : {}),
          ...(description.trim().length > 0
            ? { description: description.trim() }
            : {}),
          ...(goalCents != null ? { goal_usd_cents: goalCents } : {}),
          ...(deadlineMs != null ? { deadline_ms: deadlineMs } : {}),
          ...(Object.keys(cleanSocial).length > 0 ? { social: cleanSocial } : {}),
        },
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay show" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" style={{ maxWidth: 540 }}>
        <div className="sheet-grab" />
        <div className="sheet-head">
          <h3>New page</h3>
          <button className="sheet-x" onClick={onClose} aria-label="Close">
            <Ic name="x" size={16} />
          </button>
        </div>
        <div className="sheet-body">
          {error && (
            <div style={{ marginBottom: 14 }}>
              <Banner tone="fail">{error}</Banner>
            </div>
          )}

          {/* Kind picker */}
          <div className="field">
            <label>Page type</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
                marginTop: 4,
              }}
            >
              {(["tip", "donate", "fund"] as PageKind[]).map((k) => {
                const selected = kind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    style={{
                      padding: "12px 14px",
                      borderRadius: "var(--r-md)",
                      border: selected
                        ? "2px solid var(--brand-500)"
                        : "1px solid var(--line-2)",
                      background: selected ? "var(--brand-50)" : "var(--surface)",
                      color: "var(--ink)",
                      cursor: "pointer",
                      textAlign: "left",
                      font: "inherit",
                      transition: "all .14s var(--ease)",
                    }}
                  >
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                      {KIND_LABEL[k]}
                    </div>
                    <div
                      style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}
                    >
                      shieldz.cash/{KIND_PATH[k]}/&hellip;
                    </div>
                  </button>
                );
              })}
            </div>
            <p
              style={{
                fontSize: 12.5,
                color: "var(--ink-3)",
                margin: "10px 0 0",
                lineHeight: 1.5,
              }}
            >
              {KIND_DESCRIPTION[kind]}
            </p>
          </div>

          {/* Slug */}
          <div className="field" style={{ marginTop: 16 }}>
            <label htmlFor="slug">URL slug</label>
            <div
              style={{
                display: "flex",
                gap: 4,
                alignItems: "center",
                fontFamily: "var(--font-mono)",
                fontSize: 13,
              }}
            >
              <span style={{ color: "var(--ink-4)" }}>
                shieldz.cash/{KIND_PATH[kind]}/
              </span>
              <input
                id="slug"
                className="input mono"
                style={{ flex: 1, fontSize: 13 }}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={kind === "tip" ? "alice" : "save-the-bees"}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>

          {/* Title (optional for tip, required for donate/fund) */}
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="title">
              Title{" "}
              {kind === "tip" && (
                <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>
                  (optional)
                </span>
              )}
            </label>
            <input
              id="title"
              className="input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                kind === "tip"
                  ? "Tip me on Twitter"
                  : kind === "donate"
                    ? "Support open-source maintenance"
                    : "Ship version 2 of our open-source library"
              }
              maxLength={120}
            />
          </div>

          {/* Description */}
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="description">
              Description{" "}
              <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>
                (optional)
              </span>
            </label>
            <textarea
              id="description"
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A few sentences shown above the amount input."
              rows={3}
              maxLength={4000}
              style={{ resize: "vertical" }}
            />
          </div>

          {/* Fundraiser-only fields */}
          {kind === "fund" && (
            <>
              <div className="field" style={{ marginTop: 14 }}>
                <label htmlFor="goal">Goal (USD)</label>
                <div
                  style={{ display: "flex", gap: 6, alignItems: "center" }}
                >
                  <span
                    style={{ fontSize: 16, fontWeight: 600, color: "var(--ink-3)" }}
                  >
                    $
                  </span>
                  <input
                    id="goal"
                    className="input mono"
                    type="text"
                    inputMode="decimal"
                    value={goalUsd}
                    onChange={(e) => setGoalUsd(e.target.value)}
                    placeholder="5000"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label htmlFor="deadline">Deadline (days from now)</label>
                <input
                  id="deadline"
                  className="input"
                  type="text"
                  inputMode="numeric"
                  value={deadlineDays}
                  onChange={(e) => setDeadlineDays(e.target.value)}
                  placeholder="30"
                />
              </div>
            </>
          )}

          {/* Social / web links */}
          <div style={{ marginTop: 18 }}>
            <SocialFields value={social} onChange={setSocial} />
          </div>

          <div
            style={{
              marginTop: 14,
              fontSize: 12.5,
              color: "var(--ink-3)",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <Ic name="info" size={14} />
            Add a page image after creating — open the page and tap “Edit”.
          </div>

          <Btn
            block
            onClick={() => void handleCreate()}
            loading={busy}
            style={{ marginTop: 22 }}
          >
            Publish page
          </Btn>
          <Btn variant="ghost" block onClick={onClose} style={{ marginTop: 6 }}>
            Cancel
          </Btn>
        </div>
      </div>
    </div>
  );
}

/** Trim a social map to non-empty entries; backend validates the URLs. */
function cleanSocialMap(social: PageSocial): PageSocial {
  const out: PageSocial = {};
  for (const { key } of PAGE_SOCIAL_FIELDS) {
    const v = (social[key] ?? "").trim();
    if (v.length > 0) out[key] = v;
  }
  return out;
}

/** The preset social/web link inputs, shared by create + edit. */
function SocialFields({
  value,
  onChange,
}: {
  value: PageSocial;
  onChange: (next: PageSocial) => void;
}) {
  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 500 }}>
        Social &amp; web links{" "}
        <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>(optional)</span>
      </label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
        {PAGE_SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
          <input
            key={key}
            className="input"
            type="url"
            inputMode="url"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label={label}
            placeholder={`${label} — ${placeholder}`}
            value={value[key] ?? ""}
            onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          />
        ))}
      </div>
      <div style={{ fontSize: 12, color: "var(--ink-4)", marginTop: 6 }}>
        Shown as icons on the page. Use full https URLs.
      </div>
    </div>
  );
}

/** Edit an existing page: title, description, social links, and image. */
function EditPageSheet({
  page,
  onClose,
  onSaved,
}: {
  page: MerchantPage;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(page.config.title ?? "");
  const [description, setDescription] = useState(page.config.description ?? "");
  const [social, setSocial] = useState<PageSocial>(page.config.social ?? {});
  const [imageUrl, setImageUrl] = useState<string | undefined>(page.config.image_url);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPickImage = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setImgBusy(true);
    try {
      const r = await uploadPageImage(page.id, file);
      setImageUrl(r.image_url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setImgBusy(false);
    }
  };

  const onRemoveImage = async () => {
    setError(null);
    setImgBusy(true);
    try {
      await deletePageImage(page.id);
      setImageUrl(undefined);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setImgBusy(false);
    }
  };

  const handleSave = async () => {
    if (page.kind !== "tip" && title.trim().length === 0) {
      setError("Title is required for donation and fundraiser pages.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const cleanSocial = cleanSocialMap(social);
      // Preserve kind-specific config (goal/deadline/amounts) the editor
      // doesn't surface; only override the fields we manage here.
      await updatePage(page.id, {
        config: {
          ...page.config,
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          social: Object.keys(cleanSocial).length > 0 ? cleanSocial : undefined,
          image_url: imageUrl,
        },
        enabled: page.enabled,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay show" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" style={{ maxWidth: 540 }}>
        <div className="sheet-grab" />
        <div className="sheet-head">
          <h3>Edit {KIND_LABEL[page.kind].toLowerCase()}</h3>
          <button className="sheet-x" onClick={onClose} aria-label="Close">
            <Ic name="x" size={16} />
          </button>
        </div>
        <div className="sheet-body">
          {error && (
            <div style={{ marginBottom: 14 }}>
              <Banner tone="fail">{error}</Banner>
            </div>
          )}

          {/* Image */}
          <div className="field">
            <label>Page image</label>
            {imageUrl ? (
              <div style={{ marginTop: 6 }}>
                <img
                  src={resolveImageUrl(imageUrl)}
                  alt=""
                  style={{
                    width: "100%",
                    maxHeight: 180,
                    objectFit: "cover",
                    borderRadius: 12,
                    display: "block",
                    border: "1px solid var(--line-2)",
                  }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <label className="btn btn-ghost" style={{ cursor: "pointer" }}>
                    {imgBusy ? "Uploading…" : "Replace"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      style={{ display: "none" }}
                      onChange={(e) => void onPickImage(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  <Btn variant="ghost" onClick={() => void onRemoveImage()} loading={imgBusy}>
                    Remove
                  </Btn>
                </div>
              </div>
            ) : (
              <label
                className="btn btn-secondary"
                style={{ cursor: "pointer", marginTop: 6, display: "inline-flex" }}
              >
                <Ic name="plus" size={14} />
                {imgBusy ? "Uploading…" : "Upload image (PNG/JPEG/WebP, ≤4 MB)"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: "none" }}
                  onChange={(e) => void onPickImage(e.target.files?.[0] ?? null)}
                />
              </label>
            )}
          </div>

          {/* Title */}
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="edit-title">
              Title{" "}
              {page.kind === "tip" && (
                <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>(optional)</span>
              )}
            </label>
            <input
              id="edit-title"
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
            />
          </div>

          {/* Description */}
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="edit-desc">
              Description{" "}
              <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              id="edit-desc"
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={4000}
              style={{ resize: "vertical" }}
            />
          </div>

          {/* Social */}
          <div style={{ marginTop: 18 }}>
            <SocialFields value={social} onChange={setSocial} />
          </div>

          <Btn block onClick={() => void handleSave()} loading={busy} style={{ marginTop: 22 }}>
            Save changes
          </Btn>
          <Btn variant="ghost" block onClick={onClose} style={{ marginTop: 6 }}>
            Cancel
          </Btn>
        </div>
      </div>
    </div>
  );
}
